import { useState, useCallback, useMemo, useEffect } from "react";

import { UNIQUE_PLAYERS } from '../data/players';
import { TIER_MAPS } from '../data/tiers';
import { getAdpPosRank } from '../data/adpData';
import { hotListMultiplier } from '../data/hotList';
import { projectPoints, projectGames, displayStats } from '../utils/projectionSource';
import { computeLeagueMarketProfile, buildLeaguePriceMultipliers } from '../utils/leagueMarket';
import { buildUserPositionMultipliers } from '../utils/positionShares';
import { marketAnchorFromPriceMap, resolveMarketAnchor } from '../utils/marketAnchor';
import { isQbPremiumFormat } from '../utils/leagueFormat';

// How many players at each position this league will actually draft, from
// teams × roster size. THE source of truth for these fractions — the price
// tiers below are built off it, and the cheat sheet's print depth reads it
// too. Pure and settings-only so non-hook callers can use it without
// duplicating the fractions (two copies would drift).
export function computeDraftedCounts(settings) {
  // QB-premium, not SFLEX-specific: a 2QB league drafts the deep QB pool for
  // the same reason a superflex one does — mandatory second starters.
  const qbPremium = isQbPremiumFormat(settings.positions);
  const rosterSize = Object.values(settings.positions).reduce((s, v) => s + v, 0);
  const totalDrafted = settings.teams * rosterSize;
  return {
    QB: Math.ceil(totalDrafted * (qbPremium ? 0.22 : 0.15)),
    RB: Math.ceil(totalDrafted * 0.27),
    WR: Math.ceil(totalDrafted * 0.33),
    TE: Math.ceil(totalDrafted * 0.10),
  };
}

// How far past the drafted pool the PRINTED cheat sheet runs, per position.
// Deliberately an overshoot: consensus rankings diverge most at the tail, so
// the end of the pool is exactly where a user is most likely to miss their guy
// — stopping at the drafted count is the worst available place to stop. RB/WR
// carry the most because they are the FLEX positions and where late bidding
// happens. Lives here, next to the counts it multiplies, so there is one place
// to look and nothing to drift.
const PRINT_DEPTH_MULT = { QB: 1.25, RB: 1.65, WR: 1.65, TE: 1.5 };

export function computePrintDepth(settings) {
  const drafted = computeDraftedCounts(settings);
  const out = {};
  for (const pos of Object.keys(PRINT_DEPTH_MULT)) {
    out[pos] = Math.round((drafted[pos] || 0) * PRINT_DEPTH_MULT[pos]);
  }
  return out;
}

// activeLeague is optional and only consulted when it opts in via
// `calibrate === true`. Callers that don't pass it (and every league saved
// before the flag existed) get byte-identical prices to before.
export default function usePricing(settings, userTierOverrides, activeLeague = null) {
  // ── Custom override state ─────────────────────────────────────────────────
  const [customStats, setCustomStats] = useState(() => {
    try { return JSON.parse(localStorage.getItem("auctionArchitect_customStats")) || {}; }
    catch { return {}; }
  });
  const [expandedPlayer, setExpandedPlayer] = useState(null);

  // ── Persistence effects ───────────────────────────────────────────────────
  // One-time cleanup of the removed price-edit feature's storage
  useEffect(() => {
    localStorage.removeItem("auctionArchitect_customValues");
  }, []);

  useEffect(() => {
    localStorage.setItem("auctionArchitect_customStats", JSON.stringify(customStats));
  }, [customStats]);

  // ── Projections ───────────────────────────────────────────────────────────
  // Both of these now resolve through src/utils/projectionSource.js — the single
  // projection layer (user save → DEFAULT_PROJECTIONS → market curve). See that
  // file's header for the tier order and the DEF/K carve-out.
  //
  // getPlayerStats is the DISPLAY surface only (PlayerList's expanded stat rows,
  // keyed by STAT_LABELS), so it takes the resolved line translated back into
  // engine vocabulary. Points are never computed from it — getPlayerProj scores
  // the untranslated line — but it is the same resolved line, so the stat rows a
  // user reads now reconcile with the PROJ number above them. They did not before.
  const getPlayerStats = useCallback((player) => {
    const key = `${player.name}-${player.pos}`;
    const base = displayStats(player);
    const overrides = customStats[key] || {};
    return { ...base, ...overrides };
  }, [customStats]);

  // 🔴 `customStats` no longer reaches this number. It used to, via getPlayerStats.
  // That is inert TODAY — `setCustomStats` has exactly one call site and it is a
  // DELETE (:394 below), so nothing ever writes an override — but it is a real
  // semantic change, not a cleanup. If the stat editor is ever wired up, an
  // override will move the stat rows above and NOT this number, because overrides
  // are in engine vocabulary and this scores the projlab line. Wire it here (with
  // a reverse vocabulary map) in the same commit that wires the editor.
  const getPlayerProj = useCallback(
    (player) => projectPoints(player, settings.scoring),
    [settings.scoring]
  );

  // 🔴 THE DIVISOR THAT BELONGS TO THE NUMBER ABOVE. getPlayerProj returns SEASON
  // points over a season the projection defines — 15 games for most of the house
  // set, whatever the stints say for a user's save, 17 for DEF/K. Any per-game
  // display divides by THIS, never by a literal 17: the two travel together, and
  // a rate computed from one and a constant is ~12% low on every skill player.
  // No `scoring` dependency on purpose — the lens changes the points, never the
  // games they were scored over.
  const getPlayerGames = useCallback((player) => projectGames(player), []);

  // VORP: replacement level = last ROSTERED player (starters + bench) at each position
  const replacementLevels = useMemo(() => {
    const teams = settings.teams;
    const pos = settings.positions;
    // DELIBERATELY still the literal SFLEX test, NOT isQbPremiumFormat. This
    // flag drives two things and 2QB wants opposite answers from them:
    //   - the bench allocation split below (arguably 2QB wants the SF numbers)
    //   - `levels.SFLEX` at the bottom, which getPlayerVorp then uses to take
    //     max(proj - QBreplacement, proj - flexReplacement) for every QB.
    // The second is wrong for 2QB. A 2QB roster already carries its demand in
    // rosteredCounts.QB = 2 x teams, so the QB replacement level is deep and
    // correct on its own; layering the flex-replacement max on top would count
    // the same second starter twice. Swapping this needs its own measurement,
    // not a rename. See docs/planning/2qb-format-brief-aug14.md ("verify
    // semantics before swapping").
    const isSflex = (pos.SFLEX || 0) > 0;
    const benchPerTeam = pos.BN || 0;
    const totalBench = benchPerTeam * teams;

    const rosteredCounts = {
      QB: (pos.QB || 0) * teams,
      RB: (pos.RB || 0) * teams,
      WR: (pos.WR || 0) * teams,
      TE: (pos.TE || 0) * teams,
      DEF: (pos.DEF || 0) * teams,
      K: (pos.K || 0) * teams,
    };

    // FLEX adds to RB/WR/TE
    const totalFlex = (pos.FLEX || 0) * teams;
    rosteredCounts.RB += Math.ceil(totalFlex * 0.4);
    rosteredCounts.WR += Math.ceil(totalFlex * 0.4);
    rosteredCounts.TE += Math.floor(totalFlex * 0.2);

    // SFLEX doesn't add to QB rostered count — QB replacement level stays pure
    // SFLEX value is captured separately as the FLEX replacement level

    if (totalBench > 0) {
      if (isSflex) {
        // Superflex: teams carry 2-3 QBs total, bench heavily RB/WR
        rosteredCounts.QB += Math.round(totalBench * 0.12);
        rosteredCounts.RB += Math.round(totalBench * 0.30);
        rosteredCounts.WR += Math.round(totalBench * 0.42);
        rosteredCounts.TE += Math.round(totalBench * 0.07);
        rosteredCounts.DEF += Math.round(totalBench * 0.05);
        rosteredCounts.K += Math.round(totalBench * 0.04);
      } else {
        // Single QB: backup QB common, heavy RB/WR depth
        rosteredCounts.QB += Math.round(totalBench * 0.17);
        rosteredCounts.RB += Math.round(totalBench * 0.35);
        rosteredCounts.WR += Math.round(totalBench * 0.33);
        rosteredCounts.TE += Math.round(totalBench * 0.08);
        rosteredCounts.DEF += Math.round(totalBench * 0.04);
        rosteredCounts.K += Math.round(totalBench * 0.03);
      }
    }

    const levels = {};
    for (const [position, count] of Object.entries(rosteredCounts)) {
      const playersAtPos = UNIQUE_PLAYERS
        .filter(p => p.pos === position)
        .sort((a, b) => getPlayerProj(b) - getPlayerProj(a));
      const replacementIdx = Math.min(count, playersAtPos.length) - 1;
      levels[position] = replacementIdx >= 0 ? getPlayerProj(playersAtPos[replacementIdx]) : 0;
    }

    // SFLEX replacement level = FLEX replacement level (best available RB/WR/TE after starters)
    if (isSflex) {
      const totalFlexSlots = ((pos.FLEX || 0) + (pos.SFLEX || 0)) * teams;
      const flexPool = UNIQUE_PLAYERS
        .filter(p => p.pos === "RB" || p.pos === "WR" || p.pos === "TE")
        .sort((a, b) => getPlayerProj(b) - getPlayerProj(a));
      const starterSkillSlots = rosteredCounts.RB + rosteredCounts.WR + rosteredCounts.TE;
      const sflexIdx = Math.min(starterSkillSlots + totalFlexSlots, flexPool.length) - 1;
      levels.SFLEX = sflexIdx >= 0 ? getPlayerProj(flexPool[sflexIdx]) : 0;
    }

    return levels;
  }, [settings.teams, settings.positions, getPlayerProj]);

  const getPlayerVorp = useCallback((player) => {
    const replacement = replacementLevels[player.pos] || 0;
    const proj = getPlayerProj(player);
    // In superflex, QB value = gap between QB and the flex player you'd start instead
    if (player.pos === "QB" && replacementLevels.SFLEX !== undefined) {
      const sflexReplacement = replacementLevels.SFLEX;
      return Math.max(proj - replacement, proj - sflexReplacement);
    }
    return proj - replacement;
  }, [replacementLevels, getPlayerProj]);

  // ── League calibration ────────────────────────────────────────────────────
  // Per-position multipliers derived from this league's OWN auction history,
  // applied inside dynamicPrices below. Opt-in per league: null unless the
  // league carries history AND has calibrate === true, so untoggled leagues,
  // preset leagues, and every league saved before this existed are unaffected
  // by construction — the multiplier stage doesn't run at all for them.
  //
  // This is deliberately the ONLY insertion point. dynamicPrices is read by
  // the display surfaces AND by calcAiMaxBidV2, so calibrating here moves the
  // shown price and the AI's willingness together. Anywhere else and the two
  // would disagree.
  const leagueMultipliers = useMemo(() => {
    if (!activeLeague || activeLeague.calibrate !== true) return null;
    return buildLeaguePriceMultipliers(
      computeLeagueMarketProfile(activeLeague.auctionHistory)
    );
  }, [activeLeague]);

  // ── The user's own position dials ─────────────────────────────────────────
  // Four sliders that say "QBs go for more in my league" without importing
  // anything. Same rail as the league-history multipliers above and the same
  // single insertion point, so the shown price and the AI's willingness move
  // together — that is the whole reason this feature rides here and nowhere
  // else.
  //
  // buildUserPositionMultipliers returns NULL when every dial is at rest, so an
  // untouched board skips the multiply entirely and stays bit-identical.
  const userMultipliers = useMemo(
    () => buildUserPositionMultipliers(settings.positionDials),
    [settings.positionDials]
  );

  // 🔴 THE USER WINS. Ben's decision: manual sliders override imported league
  // history where both exist. Never silently — `dialsOverrideLeague` below is
  // what chunk 3 renders to say so out loud, because one writer quietly
  // displacing another is how the tier-override contamination bug happened.
  const effectiveMultipliers = userMultipliers ?? leagueMultipliers;
  const dialsOverrideLeague = !!userMultipliers && activeLeague?.calibrate === true;


  // ── Dynamic pricing ───────────────────────────────────────────────────────
  // Tier-based pricing calibrated from real auction data ($200/10-team baseline)
  const dynamicPrices = useMemo(() => {
    // THE reported bug (r/ff 1vnrwn3): 2QB rooms were reading QB_1QB. The
    // QB_SFLEX curve is the v1 mapping for them — the 41-draft corpus says 2QB
    // rooms clear HOTTER than this curve's top (~$44 sticker vs Allen ~$50s
    // cleared), because nobody can punt a mandatory second QB. A 2QB-specific
    // band is a measured decision for later; 41 drafts is not a tuning corpus.
    const qbPremium = isQbPremiumFormat(settings.positions);
    // Passed field-by-field, not as `settings`, so the dep array below stays
    // the precise set this memo actually reads.
    const { QB: qbDrafted, RB: rbDrafted, WR: wrDrafted, TE: teDrafted } =
      computeDraftedCounts({ positions: settings.positions, teams: settings.teams });

    const buildTiers = (drafted, priceTiers) => {
      const tiers = [];
      let cumRank = 0;
      for (let i = 0; i < priceTiers.length; i++) {
        const [frac, top, bottom] = priceTiers[i];
        const isLast = i === priceTiers.length - 1;
        const maxRank = isLast ? drafted : Math.max(cumRank + 1, Math.round(drafted * frac));
        cumRank = maxRank;
        tiers.push([maxRank, top, bottom]);
      }
      tiers.push([Infinity, 1, 1]);
      return tiers;
    };

    const TIERS = {
      RB: buildTiers(rbDrafted, [
        [0.04, 48, 46], [0.10, 38, 36], [0.18, 28, 26], [0.28, 20, 18],
        [0.38, 14, 12], [0.46, 9, 7], [0.52, 5, 4], [1.0, 2, 1],
      ]),
      WR: buildTiers(wrDrafted, [
        [0.04, 44, 42], [0.10, 36, 34], [0.17, 26, 24], [0.26, 18, 16],
        [0.35, 12, 10], [0.43, 7, 5], [0.50, 4, 3], [1.0, 2, 1],
      ]),
      QB_1QB: buildTiers(qbDrafted, [
        [0.04, 30, 30], [0.12, 18, 18], [0.20, 10, 10], [0.30, 6, 6], [0.42, 4, 4], [1.0, 2, 1],
      ]),
      QB_SFLEX: buildTiers(qbDrafted, [
        [0.06, 44, 42], [0.14, 34, 32], [0.23, 26, 24], [0.33, 18, 16],
        [0.43, 12, 10], [0.53, 7, 5], [0.65, 4, 3], [1.0, 2, 1],
      ]),
      TE: buildTiers(teDrafted, [
        [0.06, 31, 31], [0.13, 25, 25], [0.19, 19, 19], [0.31, 12, 11],
        [0.44, 8, 7], [0.50, 4, 4], [1.0, 2, 1],
      ]),
      DEF: [[3, 2, 2], [Infinity, 1, 1]],
      K: [[Infinity, 1, 1]],
    };

    const passTDMult = settings.scoring.passTD === 6 ? 1.0 : settings.scoring.passTD === 4 ? 0.85 : 0.92;
    const pprVal = settings.scoring.receptions || 0;
    const teMult = 1 + (pprVal - 0.5) * 0.3;
    const tepMult = 1 + ((settings.scoring.recBonusTE || 0) * 0.5);
    const wrMult = 1 + (pprVal - 0.5) * 0.1;
    const rbMult = 1 + (pprVal - 0.5) * -0.05;
    const applyMult = (tiers, mult) => tiers.map(([maxRank, top, bottom]) => [maxRank, top * mult, bottom * mult]);
    TIERS.QB_1QB = applyMult(TIERS.QB_1QB, passTDMult);
    TIERS.QB_SFLEX = applyMult(TIERS.QB_SFLEX, passTDMult);
    TIERS.TE = applyMult(applyMult(TIERS.TE, teMult), tepMult);
    TIERS.WR = applyMult(TIERS.WR, wrMult);
    TIERS.RB = applyMult(TIERS.RB, rbMult);

    const getTierPrice = (tiers, rank) => {
      let prevMax = 0;
      for (const [maxRank, top, bottom] of tiers) {
        if (rank <= maxRank) {
          const tierSize = maxRank === Infinity ? 1 : maxRank - prevMax;
          const posInTier = rank - prevMax - 1;
          if (tierSize <= 1) return top;
          const frac = posInTier / (tierSize - 1);
          return top - frac * (top - bottom);
        }
        prevMax = maxRank;
      }
      return 1;
    };

    const budgetScale = settings.budget / 200;
    const raw = [];
    UNIQUE_PLAYERS.forEach(p => {
      const effectivePosRank = getAdpPosRank(p.name) || p.posRank;
      const rankNum = parseInt((effectivePosRank || "1").replace(/[A-Z]+/, '')) || 1;
      let price;
      const overrideKey = `${p.pos}:${p.name}`;
      const overrideTier = userTierOverrides[overrideKey];
      if (overrideTier !== undefined && TIER_MAPS[p.pos]) {
        const tierDef = TIER_MAPS[p.pos].find(t => t.tier === overrideTier);
        if (tierDef) {
          price = ((tierDef.floor + tierDef.ceiling) / 2) * settings.budget;
        }
      }
      if (price === undefined) {
        let tiers;
        if (p.pos === "QB") tiers = qbPremium ? TIERS.QB_SFLEX : TIERS.QB_1QB;
        else tiers = TIERS[p.pos] || [[Infinity, 1, 1]];
        price = getTierPrice(tiers, rankNum) * budgetScale;
      }
      // League calibration. Applied to BOTH the tier path and the user-override
      // path: an override says "this player is a T1", not "this player costs
      // exactly $X", so the league's market shape still applies to it.
      // Positions without a multiplier (DEF/K) pass through untouched.
      if (effectiveMultipliers) price *= (effectiveMultipliers[p.pos] ?? 1);
      // Hot list (chunk 10) — Ben's editorial layer, applied HERE so it lands
      // before the renormalization below and therefore comes out of the fixed
      // pot rather than adding money to the room. `hotListMultiplier` returns
      // null (not 1) when a player is absent, so an empty list skips the
      // multiply entirely and the board stays bit-identical.
      const hot = hotListMultiplier(p.name, p.pos);
      if (hot !== null) price *= hot;
      const key = `${p.name}-${p.pos}`;
      raw.push({ key, price });
    });

    // Normalize the board back to the league's real money. This is also what
    // conserves total dollars across calibration: the multipliers redistribute
    // spend between positions, and this step pulls the sum back to
    // budget × teams, so a league that pays up for RB pays less elsewhere
    // rather than inventing money. (The $1 floor means conservation is exact
    // only to rounding — pre-existing, and it's what keeps deep benches sane.)
    const rawTotal = raw.reduce((s, r) => s + r.price, 0);
    const targetTotal = settings.budget * settings.teams;
    const scale = rawTotal > 0 ? targetTotal / rawTotal : 1;
    const map = new Map();
    raw.forEach(r => {
      map.set(r.key, Math.max(1, Math.round(r.price * scale)));
    });
    // One-source-of-truth rider: the bid engine's valuation core anchors to
    // TIER_MAPS, not to this map, so a calibrated league must ship its
    // multipliers WITH the map — otherwise the display and the bidders
    // disagree (the Gibbs-$72-shown/$62-sold seam, docs/bugs/
    // calibrated-bid-seam-diagnostic-jul26.md). Absent for every uncalibrated
    // league: the OFF path reads undefined → 1.0 by construction.
    if (effectiveMultipliers) map.__leagueMultipliers = effectiveMultipliers;
    // MARKET ANCHOR rider — the same one-source-of-truth contract, for the same
    // reason: the soft cap is a BID-side ceiling, so the user's "the top guy
    // goes for about $X in my room" answer has to travel to calcAiMaxBidV2 with
    // the board it was fitted against. Read off the map we just built, which is
    // POST-dials and post-hot-list, so a dial user's board is the denominator
    // and the echo stays coherent for free.
    //
    // Unset → buildMarketAnchor returns null → nothing is attached → the engine
    // reads `_softCap` byte-for-byte. That is the whole OFF story; there is no
    // neutral rider.
    // aug28: `resolveMarketAnchor` fills UNSET fields from the format default
    // (superflex only, as a fraction of THIS room's budget). User values always
    // win; a cleared field falls back to the format default rather than to raw
    // OFF. 1QB and 2QB resolve to the stored value unchanged, so the OFF story
    // above is untouched for them.
    const anchor = marketAnchorFromPriceMap(
      resolveMarketAnchor(settings.marketAnchor, settings.positions, settings.budget), map);
    if (anchor) map.__marketAnchor = anchor.derived;
    return map;
    // `effectiveMultipliers` replaces leagueMultipliers in the deps because it
    // is what the memo now reads; it already folds in the user dials, so a
    // slider move re-runs this. A missed dep here reads to the user as "the
    // feature does nothing".
  }, [settings.positions, settings.budget, settings.teams, settings.scoring, userTierOverrides, effectiveMultipliers, settings.marketAnchor]);

  /**
   * The same fit, exposed for the UI echo. Not a second implementation: the
   * identical pure call on the identical map, so `predicted` is the ladder the
   * `derived` values riding on that map will actually produce. (Attaching
   * `__marketAnchor` sets a property on the Map object, not an entry, so the
   * board this reads is unchanged by the attach above.)
   *
   * Null whenever the anchor is off — the component renders the hint instead.
   */
  const marketAnchor = useMemo(
    () => marketAnchorFromPriceMap(
      resolveMarketAnchor(settings.marketAnchor, settings.positions, settings.budget), dynamicPrices),
    [settings.marketAnchor, settings.positions, settings.budget, dynamicPrices],
  );

  const resetPlayerStats = useCallback((player) => {
    const key = `${player.name}-${player.pos}`;
    setCustomStats(prev => { const next = { ...prev }; delete next[key]; return next; });
  }, []);

  return {
    // State
    customStats, setCustomStats,
    expandedPlayer, setExpandedPlayer,
    // Pricing & projections
    getPlayerStats,
    getPlayerProj,
    getPlayerGames,
    replacementLevels,
    getPlayerVorp,
    dynamicPrices,
    dialsOverrideLeague,
    marketAnchor,
    // Edit actions
    resetPlayerStats,
  };
}
